import React, { useState, useEffect, useRef } from 'react';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { useInView } from '../hooks/useInView';
import { testimonials } from '../data/mock';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from './ui/carousel';

// Individual testimonial card with "Lire plus" functionality
const TestimonialCard = ({ testimonial }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [needsExpansion, setNeedsExpansion] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    if (textRef.current) {
      // Check if text is truncated (scrollHeight > clientHeight)
      const isOverflowing = textRef.current.scrollHeight > textRef.current.clientHeight;
      setNeedsExpansion(isOverflowing);
    }
  }, [testimonial.text]);

  return (
    <CarouselItem className="pl-6 md:basis-1/2 lg:basis-1/3">
      <Card className="h-full hover:shadow-lg transition-shadow duration-300 relative">
        <CardContent className="p-6">
          <Quote className="w-10 h-10 text-[#8B2942]/20 absolute top-4 right-4" />
          <div className="flex gap-1 mb-4">
            {[...Array(testimonial.rating)].map((_, i) => (
              <Star key={i} className="w-5 h-5 fill-[#8B2942] text-[#8B2942]" />
            ))}
          </div>
          <p 
            ref={textRef}
            className={`text-gray-600 mb-2 leading-relaxed italic text-sm transition-all duration-300 ${
              !isExpanded ? 'line-clamp-6' : ''
            }`}
          >
            "{testimonial.text}"
          </p>
          {needsExpansion && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-[#8B2942] text-sm font-medium hover:underline mb-4 focus:outline-none"
            >
              {isExpanded ? 'Lire moins' : 'Lire plus'}
            </button>
          )}
          <div className={`flex items-center gap-3 mt-auto ${!needsExpansion ? 'mt-4' : ''}`}>
            <div className="w-10 h-10 rounded-full bg-[#8B2942]/10 flex items-center justify-center flex-shrink-0">
              <span className="text-[#8B2942] font-semibold text-sm">
                {testimonial.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
              </span>
            </div>
            <span className="font-semibold text-gray-800 text-sm">{testimonial.name}</span>
          </div>
        </CardContent>
      </Card>
    </CarouselItem>
  );
};

const TestimonialsSection = () => {
  const [ref, isInView] = useInView({ threshold: 0.1 });
  const [carouselApi, setCarouselApi] = useState(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  useEffect(() => {
    if (!carouselApi) return;

    const updateScrollState = () => {
      setCanScrollPrev(carouselApi.canScrollPrev());
      setCanScrollNext(carouselApi.canScrollNext());
    };

    updateScrollState();
    carouselApi.on('select', updateScrollState);
    carouselApi.on('reInit', updateScrollState);

    return () => {
      carouselApi.off('select', updateScrollState);
    };
  }, [carouselApi]);

  const scrollPrev = () => carouselApi?.scrollPrev();
  const scrollNext = () => carouselApi?.scrollNext();

  return (
    <section ref={ref} className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className={`text-center mb-12 transition-all duration-400 ${
            isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-[#8B2942] mb-4">Témoignages</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Ce que nos clients disent de nous
          </p>
        </div>

        <div className="relative px-16 md:px-20">
          {/* Navigation Buttons */}
          <Button
            variant="outline"
            size="icon"
            onClick={scrollPrev}
            disabled={!canScrollPrev}
            className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 h-12 w-12 rounded-full border-[#8B2942] text-[#8B2942] hover:bg-[#8B2942] hover:text-white transition-all duration-300 ${
              !canScrollPrev ? 'opacity-50 cursor-not-allowed' : 'shadow-lg'
            }`}
          >
            <ChevronLeft className="h-6 w-6" />
          </Button>
          
          <Button
            variant="outline"
            size="icon"
            onClick={scrollNext}
            disabled={!canScrollNext}
            className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 h-12 w-12 rounded-full border-[#8B2942] text-[#8B2942] hover:bg-[#8B2942] hover:text-white transition-all duration-300 ${
              !canScrollNext ? 'opacity-50 cursor-not-allowed' : 'shadow-lg'
            }`}
          >
            <ChevronRight className="h-6 w-6" />
          </Button>

          {/* Carousel */}
          <Carousel
            setApi={setCarouselApi}
            opts={{
              align: 'start',
              loop: false,
            }}
            className="w-full"
          >
            <CarouselContent className="-ml-6">
              {testimonials.map((testimonial, index) => (
                <TestimonialCard key={testimonial.id} testimonial={testimonial} />
              ))}
            </CarouselContent>
          </Carousel>
        </div>

        {/* Testimonial count */}
        <p className="text-center text-gray-500 mt-8 text-sm">
          {testimonials.length} avis clients
        </p>
      </div>
    </section>
  );
};

export default TestimonialsSection;
